import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import countViewedNotifications from '~/components/main-navbar/notifications/count-viewed-notifications.ts';
import type { CleanNotificationWithRelations } from '~/database/supabase/notifications/types.ts';
import { useSupabase } from '~/integrations/supabase/provider.tsx';
import { cn } from '~/utils/cn.ts';

import { NotificationsDialog } from './dialog.tsx';
import {
	getNavbarNotificationsQueryOptions,
	refetchNotificationsQueryData,
	setNotificationsQueryData,
} from './query.ts';

export interface NotificationsProps {
	readonly className?: string;
	readonly command?: string[];
	readonly isDialogOpen: boolean;
	readonly isMobile?: boolean;
	readonly isOpen: boolean;
	readonly setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	readonly showKeyboardNavigation?: boolean;
	readonly textWidth: string;
}

export function Notifications({
	className,
	command,
	isDialogOpen,
	isMobile = false,
	isOpen,
	setIsOpen,
	showKeyboardNavigation,
	textWidth,
	...props
}: NotificationsProps) {
	const queryClient = useQueryClient();
	const [notificationSound] = useState(typeof Audio !== 'undefined' ? new Audio('/notification.mp3') : undefined);
	const { accessToken, supabaseClient } = useSupabase();
	const notificationsQuery = useQuery(getNavbarNotificationsQueryOptions());
	const notificationsState = notificationsQuery.data ?? [];
	const count = useMemo(() => countViewedNotifications(notificationsState), [notificationsState]);
	const setNotifications = useCallback<React.Dispatch<React.SetStateAction<CleanNotificationWithRelations[]>>>(
		(nextState) => {
			setNotificationsQueryData(queryClient, nextState);
		},
		[queryClient],
	);

	useEffect(() => {
		if (!supabaseClient || !accessToken) {
			return;
		}

		const channelName = 'news_notifications_changes';
		const channel = supabaseClient
			.channel(channelName)
			.on(
				'postgres_changes',
				{
					event: '*',
					schema: 'public',
					table: 'news_notifications',
				},
				async (payload) =>
					handleNotificationDatabaseEvent({
						notificationSound,
						payload,
						queryClient,
					}),
			)
			.subscribe();

		channel.socket.setAuth(accessToken);

		return () => {
			void supabaseClient.removeChannel(channel);
		};
	}, [accessToken, notificationSound, queryClient, supabaseClient]);

	return (
		<NotificationsDialog
			className={className}
			command={command}
			count={count}
			isDialogOpen={isDialogOpen}
			isMobile={isMobile}
			isOpen={isOpen}
			notifications={notificationsState}
			setIsOpen={setIsOpen}
			setNotifications={setNotifications}
			showKeyboardNavigation={showKeyboardNavigation}
			textWidth={textWidth}
			{...props}
		/>
	);
}

interface ToastEventProps {
	readonly id: string | number;
	readonly message: {
		readonly contents: string;
		readonly headings: string;
	};
}

function ToastEvent({ id, message }: ToastEventProps) {
	const navigate = useNavigate();

	function handleClick() {
		void navigate({
			search: { id: String(id) },
			to: '/notifications',
		});
	}

	return (
		<button
			className={cn('flex cursor-pointer flex-col border-none bg-transparent p-0 text-left')}
			onClick={handleClick}
			onKeyDown={(e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					handleClick();
				}
			}}
			type="button"
		>
			<h1 className="font-semibold">{message.headings}</h1>
			<p>{message.contents}</p>
		</button>
	);
}

interface HandleNotificationDatabaseEventProps {
	readonly notificationSound: HTMLAudioElement | undefined;
	readonly payload: RealtimePostgresChangesPayload<{ [key: string]: unknown }>;
	readonly queryClient: QueryClient;
}

async function handleNotificationDatabaseEvent({
	notificationSound,
	payload,
	queryClient,
}: HandleNotificationDatabaseEventProps) {
	if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE') {
		try {
			await refetchNotificationsQueryData(queryClient);
		} catch (error) {
			console.error(error);
		}
	}

	if (payload.eventType === 'INSERT') {
		notificationSound?.play();

		try {
			const id = String(payload.new.id);
			const response = await fetch(`/api/get-notification-content/${id}`);
			const result = (await response.json()) as {
				message: {
					contents: string;
					headings: string;
				};
			};

			toast(<ToastEvent id={id} message={result.message} />);
		} catch (error) {
			console.error(error);
		}
	}
}
